import Constants from '@/Constants';
import { BotRecommendationRequest } from '@/models/BotRecommendationRequest';
import { ActionPath, DialogFlowMessage } from '@/models/Chat';
import { GetCategoriesData } from '@/models/GetCategoriesData';
import { MessageType, PayloadType } from '@/pages/Chat/ChatModal/types';
import http from '@/services/HttpService';
import { call, put, select } from 'redux-saga/effects';
import { PayloadAction } from 'typesafe-actions';
import { ApplicationState } from '..';
import {
    completeChatFailure,
    completeChatSuccess,
    getBotRecommendationsFailure,
    getChatCategoriesFailure,
    getChatCategoriesSuccess,
    selectRecommendationContentFailure,
    selectRecommendationContentSuccess,
    sendNewEventFailure,
    sendNewEventRequest as sendNewEventRequestAction,
    sendNewEventSuccess,
} from './actions';
import { ChatActionTypes, ChatbotOutput, ChatMessage } from './types';

type SendChatMessageAction<P> = PayloadAction<ChatActionTypes.COMPLETE_SEND_CHAT_MESSAGE_REQUEST, P>;

const fillPersonName = (data: ChatbotOutput, personName: string): void => {
    data.chatbotMessagesData.forEach((messageData: DialogFlowMessage) => {
        replacePersonName(messageData, personName);
    });
};

const replacePersonName = (messageData: DialogFlowMessage, personName: string): void => {
    if (messageData.message && messageData.message.indexOf(Constants.CHATBOT_USERNAME) !== -1) {
        messageData.message = messageData.message.replace(Constants.CHATBOT_USERNAME, personName);
    }
};

export function* sendChatMessage(action: SendChatMessageAction<ChatMessage>) {
    try {
        const personName: string = yield select((state: ApplicationState) => state.identity.data.person.firstName);
        const { data } = yield call(http.post, Constants.CHAT_SEND_MESSAGE_URI, action.payload);
        fillPersonName(data, personName);
        yield put(completeChatSuccess(data));
    } catch (error) {
        yield put(completeChatFailure(error));
    }
}

export function* getChatCategoriesRequest(action: PayloadAction<ChatActionTypes.GET_CATEGORIES_REQUEST, GetCategoriesData>) {
    try {
        const { data } = yield call(http.get, Constants.CATEGORIES_URI, { params: action.payload });
        yield put(getChatCategoriesSuccess(data));
    } catch (error) {
        yield put(getChatCategoriesFailure());
    }
}

export function* getBotRecommendationRequest(action: PayloadAction<ChatActionTypes.GET_RECOMMENDATION_REQUEST, BotRecommendationRequest>) {
    try {
        const { data } = yield call(http.get, Constants.CAST_BOTS_RECOMMENDATIONS_URI, { params: action.payload });

        if (data.listContentStudentViewResult.total === 0) {
            yield put(sendNewEventRequestAction(Constants.chatbotEvents.CHATBOT_CONTENT_RECOMENDATION_EMPTY, true));
        }
        const newMessage = ({
            chatbotMessagesData: [
                {
                    message: null,
                    messageType: MessageType.PAYLOAD,
                    payload: {
                        richContent: [
                            [
                                {
                                    recomendationResult: data,
                                    type: PayloadType.ACTION,
                                    path: Constants.chatBotActionPaths.RECOMENDATION as ActionPath,
                                },
                            ],
                        ],
                    },
                },
            ],
        } as unknown) as DialogFlowMessage;

        yield put(completeChatSuccess(newMessage, true));
    } catch (error) {
        yield put(getBotRecommendationsFailure());
    }
}

export function* selectRecommendationContentRequest(
    action: PayloadAction<ChatActionTypes.SELECT_RECOMMENDATION_CONTENT_REQUEST, BotRecommendationRequest>,
) {
    try {
        const { recommendationId, contentId } = action.payload;
        yield call(
            http.put,
            Constants.CAST_BOTS_SELECT_RECOMMENDATION_URI.replace('%r', recommendationId.toString()).replace('%c', contentId.toString()),
        );
        yield put(selectRecommendationContentSuccess());
    } catch (error) {
        yield put(selectRecommendationContentFailure());
    }
}

export function* sendNewEventRequest(
    action: PayloadAction<ChatActionTypes.SEND_NEW_EVENT_REQUEST, { eventName: string; putOnMessageList: boolean }>,
) {
    try {
        const { eventName, putOnMessageList } = action.payload;
        const { data } = yield call(http.get, Constants.CAST_BOTS_EVENTS_URI.replace('%p', eventName));
        const personName: string = yield select((state: ApplicationState) => state.identity.data.person.firstName);
        if (putOnMessageList) {
            replacePersonName(data, personName);
            yield put(completeChatSuccess(data));
        } else {
            fillPersonName(data, personName);
            yield put(sendNewEventSuccess(data));
        }
    } catch (error) {
        yield put(sendNewEventFailure());
    }
}
